import React, { useEffect } from 'react';
import { sortBy, keyBy, groupBy, range, pick } from 'lodash';
import { format as formatDate, addHours, set as setDate } from 'date-fns';
import formatDistanceToNow from 'date-fns/formatDistanceToNow';
import classnames from 'classnames';
import ja from 'date-fns/locale/ja';
import { toast } from 'react-toastify';
import { Button, } from 'reactstrap';
import qs from 'qs';
import exifr from 'exifr';

import firebase from '../../firebase';
import { resizeImage } from '../../util';
import AdminPage from '../hocs/AdminPage';
import useAppTitle from '../hooks/useAppTitle';
import useDocumentSubscription from '../hooks/useDocumentSubscription';
import useCollectionSubscriptionInTenant from '../hooks/useCollectionSubscriptionInTenant';
import useCollectionSubscription from '../hooks/useCollectionSubscription';
import useTenant from '../hooks/useTenant';
import useQueryParams from '../hooks/useQueryParams';
import LectureSelector from '../LectureSelector';
import TenantLink from '../TenantLink';
import LectureDetailModal from '../modals/LectureDetailModal';
import useCollectionsFetchInTenant from '../hooks/useCollectionsFetchInTenant';
import ModalButton from '../ModalButton';
import PicsFormModal from '../modals/PicsFormModal';
import FilesUploadButton from '../FilesUploadButton';
import { staffRoles } from '../../shared/config';

const db = firebase.firestore();
const usersRef = db.collection('users');
const eventsRef = db.collection('events');

export default AdminPage(function AdminLecture(props) {
  const {
    match: {
      params: { eventId, lectureId },
    },
    addBreadNavValues,
  } = props;
  const eventRef = eventsRef.doc(eventId);
  const event = useDocumentSubscription(eventRef, [eventId]);
  const lectures = useCollectionSubscriptionInTenant(eventRef.collection('lectures'), [eventId]);
  const lecture = lectures.find(_ => _.id === lectureId);
  const lectureFrameImagesRef = eventRef.collection('lectureFrameImages').doc(lectureId);
  const lectureFrameImagesDoc = useDocumentSubscription(lectureFrameImagesRef, [eventId, lectureId]);
  const [dragOverFrameIndex, setDragOverFrameIndex] = React.useState(null);
  useAppTitle(['ADMIN', event?.name, (_ => _ && formatDate(_, 'yyyy/MM/dd'))(lecture?.date.toDate()), 'スケジュール'].join(' '));
  const eventProductTypes = sortBy(useCollectionSubscriptionInTenant(db.collection('eventProductTypes')), _ => _.createdAt.toDate());
  const lectureTypes = sortBy(useCollectionSubscriptionInTenant(db.collection('lectureTypes')), _ => _.createdAt.toDate());
  const eventProductTypesById = keyBy(eventProductTypes, 'id');
  const lectureTypesById = keyBy(lectureTypes, 'id');
  const entries = useCollectionSubscription(eventRef.collection('entries').where('lectureIds', 'array-contains', lectureId).orderBy('createdAt'), [eventId, lectureId]);
  const entryFrames = entries
    .map((e) => e.frames.map((_) => ({ ..._, entry: e })))
    .reduce((x, y) => [...x, ...y], [])
    .filter(_ => _.lectureId === lectureId);
  const entryFramesGroupedByFrameIndex = groupBy(
    entryFrames,
    'frameIndex'
  );
  const { frames: lectureFrames = [] } = lecture || {};
  const staffs = useCollectionsFetchInTenant(['admin', 'staff'].map(_ => usersRef.where('role', '==', _)));
  useEffect(() => {
    addBreadNavValues({ event, lecture });
  }, [event, lecture]);

  const entryIds = lectureFrames
    .map((lectureFrame, frameIndex) => {
      const { capacity = 0 } = lectureFrame;
      const entryFrames = entryFramesGroupedByFrameIndex[frameIndex] || [];
      return [
        ...range(0, capacity).map((seatIndex) => {
          const { entry } = entryFrames.filter((_) => _.entry?.cancelledAt == null)[seatIndex] || {};
          return entry?.id;
        }),
        ...entryFrames
          .filter((_) => _.entry?.cancelledAt != null)
          .map((entryFrame, i) => {
            const { entry } = entryFrame;
            return entry?.id;
          }),
      ];
    })
    .flat()
    .filter((_) => _);

  const handleBulkUpload = async (files) => {
    if (files.length === 0) return;

    const validateFiles = Array.from(files).every(_ => _.type.match(/^image/));
    if (!validateFiles) {
      toast.error('画像以外が選択されています。');
      return;
    }

    try {
      toast.info(`${files.length}枚の画像を処理中...`);

      const existingImages = lectureFrameImagesDoc?.images || [];

      const results = await Promise.all(
        Array.from(files).map(async (file) => {
          try {
            // 重複チェック: ファイル名 + サイズ
            const isDuplicate = existingImages.some(
              img => img.name === file.name && img.size === file.size
            );
            if (isDuplicate) {
              return { file, error: '重複ファイル' };
            }

            const exifData = await exifr.parse(file);
            const photoDate = exifData?.DateTimeOriginal || exifData?.DateTime || exifData?.CreateDate || new Date(file.lastModified);

            if (!photoDate) {
              return { file, error: '日時情報なし' };
            }

            const matchedFrameIndex = lectureFrames.findIndex((frame) => {
              const dateStr = formatDate(lecture.date.toDate(), 'yyyy-MM-dd');
              const startTimeStr = formatDate(frame.startAt.toDate(), 'HH:mm:ss');
              const endTimeStr = formatDate(frame.endAt.toDate(), 'HH:mm:ss');

              const startDateTime = new Date(`${dateStr} ${startTimeStr}`).getTime();
              const endDateTime = new Date(`${dateStr} ${endTimeStr}`).getTime();
              const photoTime = new Date(photoDate).getTime();

              return photoTime >= startDateTime && photoTime <= endDateTime;
            });

            if (matchedFrameIndex === -1) {
              return { file, photoDate, frameIndex: -1 }; // 枠外として-1を使用
            }

            return { file, photoDate, frameIndex: matchedFrameIndex };
          } catch (e) {
            console.error('日時解析エラー:', e);
            return { file, error: '日時解析失敗' };
          }
        })
      );

      const successFiles = results.filter(_ => _.frameIndex !== undefined);
      const errorFiles = results.filter(_ => _.error);
      const duplicateFiles = errorFiles.filter(_ => _.error === '重複ファイル');
      const outsideFrameFiles = successFiles.filter(_ => _.frameIndex === -1);

      if (duplicateFiles.length > 0) {
        toast.info(`${duplicateFiles.length}枚の重複ファイルをスキップしました`);
      }

      if (outsideFrameFiles.length > 0) {
        toast.info(`${outsideFrameFiles.length}枚の画像を「枠外」として登録します`);
      }

      if (errorFiles.length > duplicateFiles.length) {
        const otherErrors = errorFiles.length - duplicateFiles.length;
        console.warn('処理できなかったファイル:', errorFiles);
        toast.warning(`${otherErrors}枚の画像に日時情報がありません`);
      }

      if (successFiles.length === 0) {
        toast.error('アップロード可能な画像がありませんでした');
        return;
      }

      const groupedByFrame = groupBy(successFiles, 'frameIndex');

      await Promise.all(
        Object.entries(groupedByFrame).map(async ([frameIndex, fileInfos]) => {
          const storagePath = `lectures/${lectureId}/frames/${frameIndex}/images`;

          await Promise.all(
            fileInfos.map(async ({ file, photoDate }) => {
              const timestamp = new Date().getTime();
              const path = `${storagePath}/${timestamp}_${file.name}`;
              const storageRef = firebase.storage().ref();
              const fileRef = storageRef.child(path);
              await fileRef.put(file, { contentType: file.type });
              const downloadUrl = await fileRef.getDownloadURL();

              // サムネイル生成とアップロード
              const resizedFile = await resizeImage(file, 800);
              const thumbPath = `${storagePath}/thumb_800_${timestamp}_${file.name}`;
              const thumbRef = storageRef.child(thumbPath);
              await thumbRef.put(resizedFile, { contentType: file.type });
              const thumbnailUrl = await thumbRef.getDownloadURL();

              await lectureFrameImagesRef.set(
                {
                  images: firebase.firestore.FieldValue.arrayUnion({
                    frameIndex: parseInt(frameIndex),
                    uploadedAt: new Date(),
                    photoDate: new Date(photoDate),
                    downloadUrl,
                    thumbnailUrl,
                    storagePath: path,
                    ...pick(file, ['name', 'type', 'size']),
                  }),
                },
                { merge: true }
              );
            })
          );
        })
      );

      toast.success(`${successFiles.length}枚の画像をアップロードしました`);
    } catch (e) {
      console.error(e);
      toast.error('アップロードに失敗しました');
    }
  };

  const renderAllImagesModalContent = () => {
    const handleDragStart = (e, image) => {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('application/json', JSON.stringify(image));
    };

    const handleDragOver = (e, frameIndex) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      setDragOverFrameIndex(frameIndex);
    };

    const handleDragLeave = (e) => {
      // 子要素への移動では発火させない
      if (e.currentTarget.contains(e.relatedTarget)) return;
      setDragOverFrameIndex(null);
    };

    const handleDrop = async (e, targetFrameIndex) => {
      e.preventDefault();
      setDragOverFrameIndex(null);

      try {
        const image = JSON.parse(e.dataTransfer.getData('application/json'));

        if (image.frameIndex === targetFrameIndex) return;

        const updatedImages = (lectureFrameImagesDoc?.images || []).map(_ =>
          _.downloadUrl === image.downloadUrl ? { ..._, frameIndex: targetFrameIndex } : _
        );

        await lectureFrameImagesRef.set({ images: updatedImages }, { merge: true });
        toast.success('画像を移動しました');
      } catch (e) {
        console.error(e);
        toast.error('移動に失敗しました');
      }
    };

    return (
      <div>
        <div className="mb-3">
          <Button>
            <label className="m-0 cursor-pointer">
              <span className="fas fa-upload mr-2" />
              画像をアップロード
              <input
                type="file"
                className="d-none"
                multiple
                accept="image/*"
                onChange={_ => handleBulkUpload(_.target.files)}
              />
            </label>
          </Button>
        </div>
        <div style={{ maxHeight: 'calc(100vh - 270px)', overflowY: 'auto' }}>
          {[...lectureFrames, null].map((frame, i) => {
            const frameIndex = frame ? i : -1;
            const images = sortBy(
              (lectureFrameImagesDoc?.images || []).filter(_ => _.frameIndex === frameIndex),
              _ => _.photoDate.toDate()
            );

            // 枠外で画像がない場合は表示しない
            if (!frame && images.length === 0) return null;

            const title = frame
              ? `${formatDate(frame.startAt.toDate(), 'HH:mm')} 〜 ${formatDate(frame.endAt.toDate(), 'HH:mm')}`
              : '枠外';

            const isDragOver = dragOverFrameIndex === frameIndex;

            return (
              <div
                key={frameIndex}
                className={`mb-4 p-3 rounded ${isDragOver ? 'border border-info' : 'bg-light border border-transparent'}`}
                style={{
                  transition: 'all 0.2s ease',
                  ...(isDragOver && { backgroundColor: '#e3f2fd' })
                }}
                onDragOver={e => handleDragOver(e, frameIndex)}
                onDragLeave={handleDragLeave}
                onDrop={e => handleDrop(e, frameIndex)}
              >
                <h5 className="mb-3">
                  {title}
                  {images.length > 0 && <span className="badge badge-info ml-2">{images.length}枚</span>}
                </h5>
                {images.length === 0 ? (
                  <div className="text-muted p-3 border border-dashed" style={{ minHeight: 100 }}>
                    画像がありません
                  </div>
                ) : (
                  <div className="d-flex flex-wrap gap-2">
                    {images.map((image) => {
                      const { downloadUrl, thumbnailUrl } = image;
                      const onClickDelete = async () => {
                        if (!window.confirm('本当に削除しますか？')) return;

                        await lectureFrameImagesRef.set(
                          {
                            images: (lectureFrameImagesDoc?.images || []).filter(_ => _ !== image),
                          },
                          { merge: true }
                        );
                      };

                      return (
                        <div
                          key={downloadUrl}
                          className="card p-3 d-flex flex-column align-items-center position-relative"
                          style={{ width: 300, cursor: 'move' }}
                          draggable
                          onDragStart={e => handleDragStart(e, image)}
                        >
                          <Button
                            className="position-absolute p-0"
                            style={{ top: 8, right: 8, width: 28, height: 28 }}
                            color="danger"
                            size="sm"
                            onClick={onClickDelete}
                          >
                            <span className="fas fa-times" />
                          </Button>
                          <a href={downloadUrl} target="_blank" rel="noopener noreferrer">
                            <img src={thumbnailUrl || downloadUrl} className="img-fluid" style={{ maxHeight: 200, cursor: 'pointer' }} />
                          </a>
                          <div className="text-muted small mt-2">
                            {formatDate(image.photoDate.toDate(), 'yyyy/MM/dd HH:mm:ss')}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const handleStartSendingImages = async () => {
    if (!window.confirm('画像送信を開始しますか？\n\nこれまでのアンケート回答者全員に画像が送信され、以降は自動的に送信されるようになります。')) {
      return;
    }

    try {
      await lecture.ref.update({ imagesSendingStarted: true });

      const entriesToUpdate = entries.filter(_ =>
        _.frames?.some(frame =>
          frame.lectureId === lectureId && frame.laterAnswers
        )
      );

      await Promise.all(
        entriesToUpdate.map(_ =>
          _.ref.update({ shouldSendImages: true })
        )
      );

      toast.success(`画像送信を開始しました（${entriesToUpdate.length}件の回答者に送信）`);
    } catch (e) {
      console.error(e);
      toast.error('画像送信の開始に失敗しました');
    }
  };

  const handleStopSendingImages = async () => {
    if (!window.confirm('画像送信を停止しますか？\n\n以降、アンケート回答時に画像が送信されなくなります。')) {
      return;
    }

    try {
      await lecture.ref.update({ imagesSendingStarted: false });
      toast.success('画像送信を停止しました');
    } catch (e) {
      console.error(e);
      toast.error('画像送信の停止に失敗しました');
    }
  };

  return (
    <div>
      <div className="admin-event container py-5 position-relative">
        <div className="d-flex justify-content-end mb-2 gap-2">
          {lecture?.imagesSendingStarted ? (
            <>
              <div className="alert alert-success mb-0 py-2 px-3 d-flex align-items-center">
                <span className="fas fa-check-circle mr-2" />
                送信開始済み
              </div>
              <Button color="warning" onClick={handleStopSendingImages}>
                <span className="fas fa-stop mr-2" />
                送信を停止
              </Button>
            </>
          ) : (
            <Button color="primary" onClick={handleStartSendingImages}>
              <span className="fas fa-paper-plane mr-2" />
              画像送信を開始
            </Button>
          )}
          <ModalButton
            title="画像"
            label="画像"
            content={renderAllImagesModalContent}
            modalProps={{ style: { minWidth: '80vw' } }}
          />
        </div>
        <div className="d-flex justify-content-center mb-3">
          <LectureSelector {...{ lectureId, eventProductTypesById, lectureTypesById, lectures }} />
        </div>
        <div>
          {lectureFrames.map((lectureFrame, frameIndex) => {
            const { startAt, endAt, capacity = 0, lectureType, pics } = lectureFrame;
            const entryFrames = entryFramesGroupedByFrameIndex[frameIndex] || [];
            const frameImages = (lectureFrameImagesDoc?.images || []).filter(_ => _.frameIndex === frameIndex);

            const onSubmitEditPics = async ({ pics }) => {
              try {
                const frames = [...lectureFrames.slice(0, frameIndex), { ...lectureFrame, pics }, ...lectureFrames.slice(frameIndex + 1)];
                await lecture.ref.update({ frames });
                toast.success('担当者を更新しました');
              } catch(e) {
                console.error(e);
                toast.error('失敗しました');
              }
            };

            const renderImagesModalContent = () => {
              const storagePath = `lectures/${lectureId}/frames/${frameIndex}/images`;
              const validateFiles = (files) => {
                if (Array.from(files).every(_ => _.type.match(/^image/))) {
                  return true;
                } else {
                  return toast.error('画像以外が選択されています。');
                }
              };
              const onUploaded = async (uploads) => {
                await Promise.all(
                  uploads.map(async ({ file, path, downloadUrl, thumbnailUrl }) => {
                    await lectureFrameImagesRef.set(
                      {
                        images: firebase.firestore.FieldValue.arrayUnion({
                          frameIndex,
                          uploadedAt: new Date(),
                          downloadUrl,
                          thumbnailUrl,
                          storagePath: path,
                          ...pick(file, ['name', 'type', 'size']),
                        }),
                      },
                      { merge: true }
                    );
                  })
                );
              };

              return (
                <div>
                  <FilesUploadButton
                    storagePath={storagePath}
                    onUploaded={onUploaded}
                    accept='image/*'
                    validate={validateFiles}
                  />
                  <div className='position-relative d-flex flex-wrap justify-content-around gap-2'>
                    {frameImages.map((image) => {
                      const { downloadUrl, thumbnailUrl } = image;
                      const onClickDelete = async () => {
                        if (!window.confirm('本当に削除しますか？')) return;

                        await lectureFrameImagesRef.set(
                          {
                            images: (lectureFrameImagesDoc?.images || []).filter(_ => _ !== image),
                          },
                          { merge: true }
                        );
                      };

                      return (
                        <div
                          key={downloadUrl}
                          className='card p-3 d-flex flex-column align-items-center'
                          style={{ width: 300 }}
                        >
                          <a href={downloadUrl} target='_blank' rel='noopener noreferrer'>
                            <img src={thumbnailUrl || downloadUrl} style={{ maxHeight: 200, maxWidth: '100%' }} />
                          </a>
                          <Button className='mt-2' color='danger' size='sm' onClick={onClickDelete}>
                            削除
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            };

            return (
              <div key={frameIndex} className="card p-3 my-2">
                <div className="d-flex align-items-center mb-2 gap-2">
                  <div>
                    {formatDate(startAt.toDate(), 'HH:mm')} 〜 {formatDate(endAt.toDate(), 'HH:mm')}
                  </div>
                  <div>{lectureTypesById[lectureType]?.name}</div>
                  <duv>{(pics || []).map(({ picId, staffRole}) => staffs.find(_ => _.id === picId)?.displayName + (staffRole ? `(${staffRoles[staffRole].label})` : '')).join(',')}</duv>
                  <ModalButton Modal={PicsFormModal} size="sm" modalProps={{ pics, staffs, onSubmit: onSubmitEditPics }}>
                    <span className="fas fa-edit" />
                  </ModalButton>
                  <Button size="sm" color='secondary' tag={TenantLink} to={`/events/${event.id}/qrcode?${qs.stringify({ force: 1, lectureId, frameIndex, })}`} target='_blank'>
                    <span className='fas fa-external-link-alt mr-1' />
                    強制申込QR
                  </Button>
                </div>
                <div className="d-flex align-items-center flex-wrap">
                  {range(0, Math.max(capacity, entryFrames.filter(_ => _.entry?.cancelledAt == null).length)).map((seatIndex) => {
                    return (
                      <LectureItem
                        key={seatIndex}
                        {...{ event, entries, lecture, lectureFrame, entryFrames, seatIndex, entryIds, eventProductTypesById, lectureTypesById, }}
                        {...props}
                      />
                    );
                  })}
                  {entryFrames
                    .filter((_) => _.entry?.cancelledAt != null)
                    .map((entryFrame, i) => {
                      return <CancelledLectureItem key={i} {...{ event, entries, lecture, entryFrame, entryIds, eventProductTypesById, lectureTypesById, }} {...props} />;
                    })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
});

function LectureItem({ event, entries, lecture, lectureFrame, entryFrames, seatIndex, entryIds, user, history, eventProductTypesById, lectureTypesById, }) {
  const { entryId } = useQueryParams();
  const tenant = useTenant();
  const { name, entry } = entryFrames.filter((_) => _.entry?.cancelledAt == null)[seatIndex] || {};
  const { priorCapacity = 0 } = lectureFrame;
  const { checkedInAt, abortedAt, isTheDay, isTemporary, } = entry || {};
  const isPrior = seatIndex < priorCapacity;
  const isCheckedIn = checkedInAt != null;
  const Container = entry ? 'button' : 'span';
  const entryIndex = entries.findIndex(_ => _.id === entry?.id) + 1;
  const onClickTemp = async (e) => {
    e.stopPropagation();
    if(!window.confirm(`キャンセルします。よろしいですか？`)) return;
    
    try {
      await entry.ref.delete();
      toast.success('キャンセルしました');
    } catch (e) {
      console.error(e);
      toast.error('失敗しました');
    }
  };

  return (
    <>
      <Container
        className={classnames('card p-2 m-1 mb-2 text-center position-relative d-flex flex-column justify-content-center', {
          'border-info': isPrior,
        })}
        style={{ width: 140, height: 77, lineHeight: 1.3 }}
        onClick={() => entry && history.push(`/${tenant.id}/admin/events/${event.id}/lectures/${lecture.id}?entryId=${entry.id}`)}
      >
        <div className="position-absolute" style={{ top: -10, left: 0 }}>
          {entryIndex > 0 && (
            <span className="badge badge-pill badge-info">
              {event?.entryIndexPrefix}{entryIndex}
             </span>
          )}
          {isCheckedIn && (
            <span className="badge badge-pill badge-info">
              チェックイン済
            </span>
          )}
        </div>
        <div className="position-absolute" style={{ bottom: -10, left: 0 }}>
          {isTheDay && (
            <span className="badge badge-pill badge-secondary">
              当日
            </span>
          )}
          {isTemporary && (
            <span className="badge badge-pill badge-secondary cursor-pointer" onClick={onClickTemp}>
              仮押さえ中
            </span>
          )}
          {abortedAt != null && (
            <span className="badge badge-pill badge-secondary">
              中止済み
            </span>
          )}
        </div>
        <span className="mt-1" style={{ color: entry ? '#007bff' : '' }}>{name}</span>
        <span className="text-muted small">{entry && entry.createdBy.displayName}</span>
        <span className="text-muted mt-1 text-right" style={{ fontSize: 9 }}>
          {entry && formatDate(entry.createdAt.toDate(), 'yyyy/MM/dd HH:mm:ss')}
        </span>
        {entry != null && entry.createdAt.toDate() > addHours(new Date(), -60) && (
          <span className="text-muted mt-1 text-right" style={{ fontSize: 9 }}>
            {
              entry.forceKey && (
                <span className="badge badge-pill badge-secondary mr-1">
                  強制申込
                </span>
              )
            }
            {formatDistanceToNow(entry.createdAt.toDate(), { locale: ja })}前
          </span>
        )}
      </Container>
      {entry != null && (
        <LectureDetailModal
          isOpen={entry.id === entryId}
          onClickClose={() => history.push(`/${tenant.id}/admin/events/${event.id}/lectures/${lecture.id}`)}
          {...{ event, user, entry, lecture, entryIds, history, eventProductTypesById, lectureTypesById, }}
        />
      )}
    </>
  );
}

function CancelledLectureItem({ event, entries, lecture, entryFrame, entryIds, user, history, eventProductTypesById, lectureTypesById, }) {
  const { entryId } = useQueryParams();
  const tenant = useTenant();
  const { name, entry } = entryFrame;
  const Container = entry ? 'button' : 'span';
  const entryIndex = entries.findIndex(_ => _.id === entry?.id) + 1;
  return (
    <>
      <Container
        className={classnames(
          'card p-2 m-1 text-center position-relative d-flex flex-column justify-content-center bg-light-grey'
        )}
        style={{ width: 140, height: 70, lineHeight: 1.3, opacity: 0.6 }}
        onClick={() => entry && history.push(`/${tenant.id}/admin/events/${event.id}/lectures/${lecture.id}?entryId=${entry.id}`)}
      >
        {entryIndex > 0 && (
          <span className="badge badge-pill badge-info position-absolute" style={{ top: -10, left: 0 }}>
            {event?.entryIndexPrefix}{entryIndex}
           </span>
        )}
        <span className="badge badge-pill badge-secondary position-absolute" style={{ top: -10, left: 30 }}>
          キャンセル済み
        </span>
        <span className="mt-1" style={{ color: entry ? '#007bff' : '' }}>{name}</span>
        <span className="text-muted small">{entry && entry.createdBy.displayName}</span>
        {entry.cancelledAt.toDate() > addHours(new Date(), -60) && (
          <span className="text-muted mt-1 text-right" style={{ fontSize: 9 }}>
            {formatDistanceToNow(entry.cancelledAt.toDate(), { locale: ja })}前
          </span>
        )}
      </Container>
      {entry != null && (
        <LectureDetailModal
          isOpen={entry.id === entryId}
          onClickClose={() => history.push(`/${tenant.id}/admin/events/${event.id}/lectures/${lecture.id}`)}
          {...{ event, user, entry, lecture, entryIds, history, eventProductTypesById, lectureTypesById, }}
        />
      )}
    </>
  );
}
